import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRotateRight,
  faDesktop,
  faMobileAlt,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import AuthContext from "../../store/auth-context";
import { TicketContext } from "../../store/ticket-context";
import signalR from "../../services/signalR";
import storage from "../../hooks/storage";
import EnterCode from "./EnterCode";
import "./VisitorList.css";

const LOADER_TIMEOUT = 2000;
const DEVICE_TIMEOUT_SECONDS = 30;
let checkInterval = null;

const VisitorList = () => {
  const authCtx = useContext(AuthContext);
  const ticketCtx = useContext(TicketContext);
  const [deviceArr, setDeviceArr] = useState([]);
  const [deviceCounterObj, setDeviceCounterObj] = useState({});
  const [isReloadDeviceList, setIsReloadDeviceList] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [disconnectedDeviceData, setDisconnectedDeviceData] = useState(null);
  const [incomingDevice, setIncomingDevice] = useState(null);
  const chatDeviceRef = useRef(ticketCtx?.ticketDeviceIdList || []);
  const waitingTimeout = useRef(null);

  const setReloadInterval = () => {
    setIsReloadDeviceList(true);
    waitingTimeout.current = setTimeout(() => {
      setIsReloadDeviceList(false);
    }, LOADER_TIMEOUT);
  };

  const clearTimer = () => {
    clearTimeout(waitingTimeout.current);
  };

  const resetReloadInterval = () => {
    clearTimer();
    setReloadInterval();
  };

  const reloadDeviceList = () => {
    resetReloadInterval();
    setIsSpinning(true);
    setTimeout(() => {
      setIsSpinning(false);
    }, 2000);
  };

  // Lazy socket init — only opens on mount of this component (which itself
  // only mounts when the agent switches to Cobrowse mode). Wipes any stale
  // singleton state from a previous session so callbacks are re-registered
  // against fresh React setters.
  useEffect(() => {
    signalR.connection = null;
    signalR.Connect(authCtx.token);
    signalR.DefineSender((data) => {
      if (data) setIncomingDevice(data);
    });
    signalR.DefineDeviceDisconnected((data) => {
      if (data) setDisconnectedDeviceData(data);
    });
    signalR.deviceInfo = null;
    signalR.replayEvent = null;
  }, [authCtx.token]);

  // Keep the ref of tagged device IDs in sync so the async signalR callback
  // reads the latest value without needing to be re-bound every render.
  useEffect(() => {
    const list = ticketCtx?.ticketDeviceIdList || [];
    if (list.length > 0) {
      chatDeviceRef.current = list;
    }
  }, [ticketCtx?.ticketDeviceIdList]);

  useEffect(() => {
    if (disconnectedDeviceData) {
      removeDevice(disconnectedDeviceData.code, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disconnectedDeviceData]);

  useEffect(() => {
    if (incomingDevice) {
      deviceData(incomingDevice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingDevice]);

  // Sweep the device list every 2s and drop any device whose last heartbeat
  // is older than 30s. Only runs while at least one device is being tracked.
  const checkDeviceOffline = (fromNewDevice) => {
    if (fromNewDevice) {
      clearInterval(checkInterval);
    }
    checkInterval = setInterval(() => {
      if (deviceCounterObj) {
        const currentTimeStamp = dayjs().unix();
        const entries = Object.entries(deviceCounterObj);
        for (const [key, value] of entries) {
          const diffInSeconds = currentTimeStamp - parseInt(value, 10);
          if (diffInSeconds > DEVICE_TIMEOUT_SECONDS) {
            removeDevice(key);
          }
        }
        if (entries.length === 0) {
          clearInterval(checkInterval);
        }
      }
    }, 2000);
  };

  const removeDevice = (userIdToRemove, isDeviceDisconnected = false) => {
    if (deviceArr?.some((element) => element.sessionID === userIdToRemove)) {
      const counterObjCopy = { ...deviceCounterObj };
      delete counterObjCopy[userIdToRemove];
      setDeviceCounterObj(counterObjCopy);
      const latestDeviceArr = deviceArr.filter(
        (element) => element.sessionID !== userIdToRemove
      );
      setDeviceArr(latestDeviceArr);
      if (disconnectedDeviceData && isDeviceDisconnected) {
        setDisconnectedDeviceData(null);
      }
    }
  };

  const deviceData = (data) => {
    if (!data) return;
    let newDevice = { ...data };
    const userIdToRemove = newDevice.sessionID;

    if (deviceArr?.some((element) => element.sessionID === userIdToRemove)) {
      // Known device — refresh its heartbeat.
      const deviceCounterObjCopy = { ...deviceCounterObj };
      deviceCounterObjCopy[userIdToRemove] = dayjs().unix();
      setDeviceCounterObj(deviceCounterObjCopy);
      checkDeviceOffline(true);
    } else {
      // New device — only add if its device_id matches a "blitzz_deviceid*"
      // tag on the current Zendesk ticket, so the sidebar only lists
      // devices belonging to this ticket's requester.
      const isChatDevice = chatDeviceRef.current.filter(
        (str) => typeof str === "string" && str.includes(newDevice.device_id)
      );
      if (isChatDevice?.length > 0) {
        const browserName = newDevice?.deviceInfo?.browser?.name;
        const osName = newDevice?.deviceInfo?.os?.name;
        if (browserName && osName) {
          newDevice.deviceName = browserName + " on " + osName;
        } else {
          newDevice.deviceName = newDevice.reference
            ? newDevice.reference
            : "Visitor " + newDevice?.sessionID;
        }
        setDeviceCounterObj((previous) => ({
          ...previous,
          [newDevice.sessionID]: dayjs().unix(),
        }));
        setDeviceArr([newDevice, ...deviceArr]);
        checkDeviceOffline(true);
      }
    }
  };

  const redirectToCobrowse = (sessionCode) => {
    const userdata = storage.get("userdata") || {};
    const domainUrl = userdata.domainUrl || window.location.origin;
    const storedToken = storage.get("token");
    const storedRefreshToken = storage.get("refreshtoken");
    const storedExpirationDate = storage.get("expirationTime");
    const remainingTime =
      new Date(storedExpirationDate).getTime() - Date.now();

    if (!storedToken || remainingTime <= 0) {
      window.location.reload();
      return;
    }

    const cobrowseUrl =
      `${domainUrl}/cobrowse/call/${sessionCode}` +
      `?t1=${encodeURIComponent(storedToken)}` +
      `&r1=${encodeURIComponent(storedRefreshToken)}`;
    window.open(cobrowseUrl, "_blank", "noopener,noreferrer");
  };

  const deviceDataList = useMemo(() => {
    return deviceArr.map((device) => {
      const isMobile = device?.deviceInfo?.os?.deviceType !== 1;
      return (
        <div className="cb-device-card" key={`device_${device?.sessionID}`}>
          <div className="cb-device-icon">
            <FontAwesomeIcon icon={isMobile ? faMobileAlt : faDesktop} />
          </div>
          <div className="cb-device-body">
            <div className="cb-device-name">{device?.deviceName}</div>
            <div className="cb-device-status">Online</div>
          </div>
          <button
            type="button"
            className="cb-connect-btn"
            onClick={() => redirectToCobrowse(device?.sessionID)}
          >
            Connect
          </button>
        </div>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceArr]);

  return (
    <div className="cb-visitor-list">
      <EnterCode redirectToCobrowse={redirectToCobrowse} />
      <div className="cb-devices-section">
        <div className="cb-devices-section-row">
          <p className="cb-device-title">Devices</p>
          <button
            type="button"
            className="cb-refresh-btn"
            onClick={reloadDeviceList}
            aria-label="Refresh devices"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} spin={isSpinning} />
          </button>
        </div>
      </div>
      {deviceArr?.length === 0 && !isReloadDeviceList && (
        <div className="cb-empty">No devices found</div>
      )}
      {deviceArr?.length > 0 && !isReloadDeviceList && deviceDataList}
      {isReloadDeviceList && <div className="cb-loading">Loading...</div>}
    </div>
  );
};

export default VisitorList;
